import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { MdCloudUpload, MdCheckCircle, MdDownload, MdMenuBook, MdSave } from 'react-icons/md';
import PageContainer from '../../../shared/layout/containers/PageContainer';
import PageContent from '../../../shared/layout/containers/PageContent';
import ErrorMessage from '../../../shared/layout/feedback/ErrorMessage';
import MainButton from '../../../shared/layout/buttons/MainButton';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import SectionTitle from '../../../shared/layout/titles/SectionTitle';
import { SearchableSelect } from '../../../shared/layout/forms/AppSelect';
import {
    analyzeCsvImport,
    uploadCsvImport,
    fetchOrgManufacturers,
    fetchOrgVenuesForImport,
    saveCsvImportMappings,
    runCsvImport,
    fetchCsvImportProgress,
    suggestCsvImportFieldMapping,
    suggestCsvImportCategoryMappings,
    saveCsvImportFieldMapping,
    searchGoogleProductCategories,
} from '../../../services/Http/Api';

// Iz backenda (CatalogTemplate); ovdje stoje da se ekran moze prikazati i prije
// prve otpreme, kad odgovora jos nema.
const TEMPLATE_URL = 'https://cdn.digiwarranty.app/dwm-uploads/partneri/dwm-katalog-template.csv';
const GUIDE_URL = 'https://cdn.digiwarranty.app/dwm-uploads/partneri/dwm-katalog-upute.md';
const CSV_FIELD_TARGETS = [
    'external_product_id', 'sku', 'sku_or_gtin', 'ean', 'gtin', 'upc', 'mpn',
    'product_name', 'manufacturer_name', 'manufacturer_external_id', 'category_name', 'category_external_id',
    'condition', 'warranty_months', 'price', 'discounted_price', 'discount_percentage', 'discount_value', 'currency',
    'discount_start_at', 'discount_end_at', 'stock_quantity', 'reserved_quantity', 'is_active', 'product_url',
    'image_url', 'additional_image_urls', 'short_description', 'product_description', 'venue_name', 'venue_external_id',
];
const CSV_REQUIRED_FIELDS = ['product_name', 'price', 'product_url'];
const CSV_IDENTITY_FIELDS = ['external_product_id', 'sku', 'sku_or_gtin', 'ean', 'gtin', 'upc'];

const ImportsPage = () => {
    const { t } = useTranslation();
    const [selectedFile, setSelectedFile] = useState(null);
    const importType = 'catalog';
    const [uploadResult, setUploadResult] = useState(null);
    const [analyzeResult, setAnalyzeResult] = useState(null);
    const [manufacturersOptions, setManufacturersOptions] = useState([]);
    const [categoryOptionSelections, setCategoryOptionSelections] = useState({});
    const [venueOptions, setVenueOptions] = useState([]);
    const [manufacturerMappings, setManufacturerMappings] = useState({});
    const [categoryMappings, setCategoryMappings] = useState({});
    // venueMappings: { [external_id]: { action: 'map'|'create'|null, venue_id?, name?, city?, address?, country_id? } }
    const [venueMappings, setVenueMappings] = useState({});
    const [mappingSaved, setMappingSaved] = useState(false);
    const [fieldMapping, setFieldMapping] = useState({});
    const [fieldMappingSuggestions, setFieldMappingSuggestions] = useState({});
    const [fieldMappingValidation, setFieldMappingValidation] = useState('');
    const [savingFields, setSavingFields] = useState(false);
    const [runSummary, setRunSummary] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadingStage, setLoadingStage] = useState('');
    const [mappingWarning, setMappingWarning] = useState('');
    const [error, setError] = useState(null);
    const [wizardStep, setWizardStep] = useState(1);
    const pollRef = useRef(null);
    const fieldMappingLockedRef = useRef(false);

    useEffect(() => {
        return () => {
            if (pollRef.current) {
                clearInterval(pollRef.current);
            }
        };
    }, []);

    const handleFileChange = (event) => {
        setSelectedFile(event.target.files?.[0] || null);
        setUploadResult(null);
        setAnalyzeResult(null);
        setManufacturerMappings({});
        setCategoryMappings({});
        setCategoryOptionSelections({});
        setVenueMappings({});
        setMappingSaved(false);
        setFieldMapping({});
        setFieldMappingSuggestions({});
        setFieldMappingValidation('');
        fieldMappingLockedRef.current = false;
        setRunSummary(null);
        setProgress(null);
        setError(null);
        setMappingWarning('');
        setWizardStep(1);
    };

    const analyzeAndSuggestCategories = async (importId) => {
        setLoadingStage(t('Imports.wizard.processing.analyzing', { defaultValue: 'Analiziramo kategorije i proizvode…' }));
        const analysis = await analyzeCsvImport(importId);
        setAnalyzeResult(analysis);
        applyAnalyzeMappings(analysis);
        await loadMappingOptions(importType);

        if ((analysis.categories || []).length > 0) {
            try {
                setLoadingStage(t('Imports.wizard.processing.categories', { defaultValue: 'Tražimo prijedloge kategorija…' }));
                await suggestCsvImportCategoryMappings(importId);
                const refreshed = await analyzeCsvImport(importId);
                setAnalyzeResult(refreshed);
                applyAnalyzeMappings(refreshed);
            } catch (suggestionError) {
                setMappingWarning(t('Imports.wizard.processing.categorySuggestionFailed', { defaultValue: 'Automatski prijedlozi kategorija trenutno nisu dostupni. Kategorije možeš odabrati ručno.' }));
            }
        }
        setWizardStep(3);
    };

    const handleUpload = async () => {
        if (!selectedFile) {
            return;
        }
        setLoading(true);
        setLoadingStage(t('Imports.wizard.processing.uploading', { defaultValue: 'Učitavamo CSV datoteku…' }));
        setError(null);
        setMappingWarning('');
        try {
            const data = await uploadCsvImport(selectedFile, importType);
            setUploadResult(data);
            let mapping = { ...(data.field_mapping || {}) };
            setFieldMapping(mapping);
            setFieldMappingSuggestions({});
            setAnalyzeResult(null);
            setRunSummary(null);
            setProgress(null);

            if (!data?.field_mapping_reused) {
                setWizardStep(2);
                setLoadingStage(t('Imports.wizard.processing.fields', { defaultValue: 'Prepoznajemo kolone i predlažemo mapiranje…' }));
                try {
                    const ai = await suggestCsvImportFieldMapping(data.import_id);
                    const validSuggestions = Object.fromEntries(
                        Object.entries(ai.suggestions || {}).filter(([target]) => CSV_FIELD_TARGETS.includes(target))
                    );
                    setFieldMappingSuggestions(validSuggestions);
                    if (!fieldMappingLockedRef.current) {
                        Object.entries(validSuggestions).forEach(([target, suggestion]) => {
                            if (suggestion?.source_field && Number(suggestion.confidence) >= 0.9) {
                                mapping[target] = suggestion.source_field;
                            }
                        });
                        setFieldMapping(mapping);
                    }
                } catch (aiError) {
                    setMappingWarning(t('Imports.wizard.processing.fieldSuggestionFailed', { defaultValue: 'Automatsko prepoznavanje nije uspjelo. Provjeri i odaberi kolone ručno.' }));
                }
            } else {
                setLoadingStage(t('Imports.wizard.processing.reusingMapping', { defaultValue: 'Koristimo ranije sačuvano mapiranje i provjeravamo podatke…' }));
                setWizardStep(3);
                await analyzeAndSuggestCategories(data.import_id);
            }
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
            setLoadingStage('');
        }
    };

    const handleAnalyze = async () => {
        if (!uploadResult?.import_id) {
            return;
        }
        setLoading(true);
        setLoadingStage(t('Imports.wizard.processing.analyzing', { defaultValue: 'Analiziramo kategorije i proizvode…' }));
        setError(null);
        try {
            setMappingSaved(false);
            setRunSummary(null);
            setProgress(null);
            await analyzeAndSuggestCategories(uploadResult.import_id);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
            setLoadingStage('');
        }
    };

    const handleSaveFieldMapping = async () => {
        if (!uploadResult?.import_id) return;
        const hasIdentity = CSV_IDENTITY_FIELDS.some((field) => fieldMapping[field]);
        const missing = [...CSV_REQUIRED_FIELDS.filter((field) => !fieldMapping[field])];
        if (!hasIdentity) missing.push('sku_or_external_product_id_or_gtin');
        if (missing.length) {
            setFieldMappingValidation(t('Imports.fieldMappingMissing', {
                defaultValue: 'Poveži naziv proizvoda, cijenu, URL proizvoda i barem jedan identifikator (SKU, EAN ili ID proizvoda).',
            }));
            return;
        }

        fieldMappingLockedRef.current = true;
        setFieldMappingValidation('');
        setSavingFields(true);
        setLoading(true);
        setLoadingStage(t('Imports.wizard.processing.savingMapping', { defaultValue: 'Čuvamo mapiranje kolona…' }));
        setError(null);
        try {
            const saved = await saveCsvImportFieldMapping(uploadResult.import_id, fieldMapping);
            setUploadResult((current) => ({ ...current, status: 'validated', field_mapping_reused: true, field_mapping: saved.field_mapping }));
            setMappingSaved(false);
            setRunSummary(null);
            setProgress(null);
            setWizardStep(3);
            await analyzeAndSuggestCategories(uploadResult.import_id);
        } catch (err) {
            setError(err);
        } finally {
            setSavingFields(false);
            setLoading(false);
            setLoadingStage('');
        }
    };

    const loadMappingOptions = async (type) => {
        try {
            const needsProductMappings = type === 'catalog';
            const needsVenues = type === 'stock' || type === 'catalog';

            const [manufacturers, venues] = await Promise.all([
                needsProductMappings ? fetchOrgManufacturers({ all: true }) : Promise.resolve([]),
                needsVenues ? fetchOrgVenuesForImport() : Promise.resolve([]),
            ]);

            setManufacturersOptions(
                (manufacturers || []).map((item) => ({ value: item.id, label: item.name }))
            );
            // venues endpoint returns paginated { data: [...] } or plain array
            const venueList = venues?.data ?? (Array.isArray(venues) ? venues : []);
            setVenueOptions(
                venueList.map((v) => ({
                    value: v.id,
                    label: `${v.name}${v.city ? ` (${v.city})` : ''}`,
                }))
            );
        } catch (err) {
            setError(err);
        }
    };

    const applyAnalyzeMappings = (analysis) => {
        const manufacturerState = {};
        (analysis?.manufacturers || []).forEach((item) => {
            const key = item.external_id || item.name;
            if (item.mapped_id) {
                manufacturerState[key] = item.mapped_id;
            } else if (item.mapped_action === 'create') {
                manufacturerState[key] = '__create__';
            } else {
                // Unknown source brands are created during import by default;
                // the retailer can still link them to an existing manufacturer.
                const normalizedName = String(item.name || '').trim().toLowerCase();
                const placeholderNames = ['unknown', 'unknown manufacturer', 'n/a', 'na', 'none', 'no brand', 'unbranded', 'generic', 'modex shop', 'modex-shop'];
                if (normalizedName && !placeholderNames.includes(normalizedName)) {
                    manufacturerState[key] = '__create__';
                }
            }
        });

        const categoryState = {};
        const categoryOptionState = {};
        (analysis?.categories || []).forEach((item) => {
            const key = item.external_id || item.name;
            if (item.mapped_id) {
                categoryState[key] = item.mapped_id;
            } else if (item.mapped_action === 'create') {
                categoryState[key] = '__create__';
            } else if (item.suggested_mapping && Number(item.suggested_mapping.confidence) >= 90) {
                // Only high-confidence Gemini suggestions can be preselected
                // for the batch confirmation. Weaker suggestions remain
                // visible for review, rather than being silently confirmed.
                categoryState[key] = `gpt:${item.suggested_mapping.id}`;
                categoryOptionState[key] = {
                    value: `gpt:${item.suggested_mapping.id}`,
                    label: `${item.suggested_mapping.path || item.suggested_mapping.name} · GPT ${item.suggested_mapping.gpt_code}`,
                };
            }
        });

        const venueState = {};
        (analysis?.venues || []).forEach((item) => {
            if (item.matched_venue_id) {
                // Auto-matched — pre-select the mapped venue
                venueState[item.external_id] = {
                    action: 'map',
                    venue_id: item.matched_venue_id,
                };
            } else {
                // Unmatched — pre-fill create form with CSV hint data
                venueState[item.external_id] = {
                    action: null,
                    name: item.name || '',
                    city: item.city || '',
                    address: '',
                    country_id: null,
                };
            }
        });

        setManufacturerMappings(manufacturerState);
        setCategoryMappings(categoryState);
        setCategoryOptionSelections(categoryOptionState);
        setVenueMappings(venueState);
    };

    const handleSaveMappings = async () => {
        if (!uploadResult?.import_id) {
            return;
        }
        const manufacturersPayload = (analyzeResult?.manufacturers || []).map((item) => {
            const key = item.external_id || item.name;
            const selected = manufacturerMappings[key];
            const action = selected === '__create__' ? 'create' : selected ? 'map' : null;
            return {
                external_id: key,
                external_name: item.name,
                digiwarranty_id: selected && selected !== '__create__' ? selected : null,
                action,
            };
        });

        const categoriesPayload = (analyzeResult?.categories || []).map((item) => {
            const key = item.external_id || item.name;
            const selected = categoryMappings[key];
            const isGpt = typeof selected === 'string' && selected.startsWith('gpt:');
            const action = selected === '__create__' ? 'create' : selected ? 'map' : null;
            return {
                external_id: key,
                external_name: item.name,
                digiwarranty_id: selected && selected !== '__create__'
                    ? (isGpt ? selected.slice(4) : selected)
                    : null,
                digiwarranty_type: isGpt ? 'category_gpt' : 'category_core',
                action,
            };
        });

        const venuesPayload = (analyzeResult?.venues || []).map((item) => {
            const m = venueMappings[item.external_id] || {};
            return {
                external_id: item.external_id,
                action: m.action || null,
                venue_id: m.action === 'map' ? (m.venue_id || null) : null,
                name: m.name || item.name || null,
                city: m.city || item.city || null,
                address: m.address || null,
                country_id: m.country_id || null,
            };
        });

        setLoading(true);
        setLoadingStage(t('Imports.wizard.processing.savingMappings', { defaultValue: 'Čuvamo mapiranja…' }));
        setError(null);
        try {
            await saveCsvImportMappings(uploadResult.import_id, {
                manufacturers: manufacturersPayload,
                categories: categoriesPayload,
                venues: venuesPayload,
            });
            setMappingSaved(true);
            setWizardStep(4);
            setRunSummary(null);
            setProgress(null);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
            setLoadingStage('');
        }
    };

    const startPolling = (importId) => {
        if (pollRef.current) {
            clearInterval(pollRef.current);
        }

        const pollProgress = async () => {
            try {
                const data = await fetchCsvImportProgress(importId);
                setProgress(data);
                const importFinished = ['staged', 'completed', 'completed_with_errors', 'completed_with_review', 'failed']
                    .includes(data?.import_status);
                if (importFinished || (data?.status && !['queued', 'processing', 'staged', 'enrichment_pending'].includes(data.status))) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                }
            } catch (err) {
                setError(err);
                clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };

        void pollProgress();
        pollRef.current = setInterval(pollProgress, 2000);
    };

    const handleRun = async () => {
        if (!uploadResult?.import_id) {
            return;
        }
        setLoading(true);
        setLoadingStage(t('Imports.wizard.processing.startingImport', { defaultValue: 'Pokrećemo uvoz…' }));
        setError(null);
        try {
            const data = await runCsvImport(uploadResult.import_id);
            setRunSummary(data);
            startPolling(uploadResult.import_id);
        } catch (err) {
            setError(err);
        } finally {
            setLoading(false);
            setLoadingStage('');
        }
    };

    const manufacturerSelectOptions = [
        { value: '__create__', label: t('Imports.createManufacturer') },
        ...manufacturersOptions,
    ];


    const venueSelectOptions = [
        { value: '__create__', label: t('Imports.createVenue') },
        ...venueOptions,
    ];

    const requiresMappings = importType === 'catalog';
    const hasVenues = (analyzeResult?.venues || []).length > 0;
    const requiresVenueMappings = (importType === 'stock' || importType === 'catalog') && hasVenues;
    // Unresolved venues are allowed (they fall back to org-level) — only require mappings saved
    const canRunImport =
        (requiresMappings && mappingSaved) ||
        (requiresVenueMappings && mappingSaved) ||
        (importType === 'discounts' && analyzeResult) ||
        (importType === 'stock' && analyzeResult && !hasVenues);
    const wizardSteps = [
        t('Imports.wizard.upload', { defaultValue: 'Učitaj CSV' }),
        t('Imports.wizard.columns', { defaultValue: 'Provjeri kolone' }),
        t('Imports.wizard.mappings', { defaultValue: 'Proizvođači i kategorije' }),
        t('Imports.wizard.review', { defaultValue: 'Pregled i uvoz' }),
    ];
    const suggestedCategoryCount = (analyzeResult?.categories || []).filter((item) => (
        !item.mapped_id && Number(item.suggested_mapping?.confidence) >= 65
    )).length;
    const visibleFieldTargets = Array.from(new Set([
        ...CSV_REQUIRED_FIELDS,
        ...CSV_IDENTITY_FIELDS,
    ]));
    const optionalFieldTargets = CSV_FIELD_TARGETS.filter((field) => !visibleFieldTargets.includes(field));
    const requiredMappedCount = CSV_REQUIRED_FIELDS.filter((target) => fieldMapping[target]).length
        + (CSV_IDENTITY_FIELDS.some((target) => fieldMapping[target]) ? 1 : 0);
    const importTotalRows = Number(progress?.total_rows ?? uploadResult?.total_rows ?? runSummary?.total_rows ?? 0);
    const importProcessedRows = Number(progress?.processed_rows ?? runSummary?.processed_rows ?? 0);
    const importProgressPercent = importTotalRows > 0
        ? Math.min(100, Math.round((importProcessedRows / importTotalRows) * 100))
        : 0;
    const renderFieldMappingRow = (target) => (
        <FieldMappingRow key={target}>
            <FieldMappingDetails>
                <FieldMappingLabel>
                    {t(`Imports.csvField.${target}`, { defaultValue: target.replace(/_/g, ' ') })}
                    {CSV_REQUIRED_FIELDS.includes(target) && <RequiredMark> *</RequiredMark>}
                </FieldMappingLabel>
                {fieldMappingSuggestions[target] && <FieldSuggestionMeta>
                    {t('Imports.fieldMappingAiSuggestion', { confidence: Math.round((fieldMappingSuggestions[target].confidence || 0) * 100) })}
                </FieldSuggestionMeta>}
                {fieldMapping[target] && uploadResult.preview?.[0]?.[fieldMapping[target]] != null && (
                    <FieldExample>{String(uploadResult.preview[0][fieldMapping[target]]).slice(0, 100)}</FieldExample>
                )}
            </FieldMappingDetails>
            <FieldMappingSelect
                value={fieldMapping[target] || ''}
                onChange={(event) => {
                    fieldMappingLockedRef.current = true;
                    setFieldMappingValidation('');
                    setFieldMapping((current) => ({ ...current, [target]: event.target.value }));
                }}
                aria-label={`${t('Imports.fieldMappingSource', { defaultValue: 'CSV column' })}: ${target}`}
            >
                <option value="">— nije mapirano —</option>
                {(uploadResult.columns_detected || []).map((header) => (
                    <option key={`${target}-${header}`} value={header}>{header}</option>
                ))}
            </FieldMappingSelect>
        </FieldMappingRow>
    );
    return (
        <PageContainer>
            <PageContent>
                <WizardProgress aria-label={t('Imports.wizard.title', { defaultValue: 'Koraci uvoza' })}>
                    {wizardSteps.map((label, index) => {
                        const step = index + 1;
                        const complete = step < wizardStep;
                        return (
                            <WizardStep key={label} $active={step === wizardStep} $complete={complete}>
                                <WizardStepNumber>{complete ? '✓' : step}</WizardStepNumber>
                                <span>{label}</span>
                            </WizardStep>
                        );
                    })}
                </WizardProgress>
                {!uploadResult && <UploadCard>
                    <UploadHeading>
                        <UploadIcon><MdCloudUpload size={25} /></UploadIcon>
                        <div>
                            <h3>{t('Imports.uploadTitle')}</h3>
                            <p>{t('Imports.uploadSubtitle')}</p>
                        </div>
                    </UploadHeading>
                    <UploadForm>
                        <UploadDropzone>
                            <FileInput
                                type="file"
                                accept=".csv,.txt"
                                onChange={handleFileChange}
                                aria-label={t('Imports.uploadButton')}
                            />
                            <UploadPrompt>
                                <strong>{selectedFile?.name || t('Imports.uploadButton')}</strong>
                                <span>{selectedFile
                                    ? `${(selectedFile.size / (1024 * 1024)).toFixed(2)} MB`
                                    : t('Imports.requiredNote')}</span>
                            </UploadPrompt>
                            <UploadBrowse>{t('Imports.chooseFile', { defaultValue: 'Odaberi datoteku' })}</UploadBrowse>
                        </UploadDropzone>
                        <MainButton type="button" onClick={handleUpload} disabled={!selectedFile || loading} icon={<MdCloudUpload />}>
                            {loading ? t('Imports.uploading') : t('Imports.uploadButton')}
                        </MainButton>
                    </UploadForm>

                    <TemplateDetails>
                        <summary>{t('Imports.template.title')}</summary>
                        <ColumnsGuideText>{t('Imports.template.description')}</ColumnsGuideText>
                        <TemplateRow>
                            <TemplateLink href={TEMPLATE_URL} download>
                                <MdDownload size={17} /> {t('Imports.template.download')}
                            </TemplateLink>
                            <TemplateLink href={GUIDE_URL} target="_blank" rel="noopener noreferrer">
                                <MdMenuBook size={17} /> {t('Imports.template.guide')}
                            </TemplateLink>
                        </TemplateRow>
                        <ColumnsGuideText>
                            <strong>{t('Imports.columnsGuide.required')}</strong>{' '}
                            {t('Imports.template.required')}
                        </ColumnsGuideText>
                    </TemplateDetails>
                </UploadCard>}

                {loading && (
                    <ProgressPanel role="status" aria-live="polite">
                        <ProgressSpinner aria-hidden="true" />
                        <div>
                            <ProgressLabel>{loadingStage || t('Imports.loading')}</ProgressLabel>
                            <ProgressSubtext>{t('Imports.wizard.processing.wait', { defaultValue: 'Ovo može potrajati nekoliko trenutaka. Ne zatvaraj ovu stranicu.' })}</ProgressSubtext>
                        </div>
                    </ProgressPanel>
                )}
                {error && <ErrorMessage>{t('Imports.error')}: {error.message}</ErrorMessage>}
                {mappingWarning && <NoteBox role="status">{mappingWarning}</NoteBox>}

                {uploadResult && (
                    <StyledCard style={{ marginTop: '1.25rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>{t('Imports.validationTitle')}</h3>
                            <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.95rem' }}>{t('Imports.validationSubtitle')}</p>
                        </div>
                        <InfoGrid>
                            <InfoItem>
                                <InfoLabel>{t('Imports.status')}</InfoLabel>
                                <InfoValue>
                                    {uploadResult.field_mapping_reused
                                        ? t('Imports.wizard.columnsReady', { defaultValue: 'Kolone prepoznate' })
                                        : t('Imports.wizard.fileUploaded', { defaultValue: 'Datoteka učitana' })}
                                </InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('Imports.totalRows')}</InfoLabel>
                                <InfoValue>{uploadResult.total_rows}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('Imports.missingColumns')}</InfoLabel>
                                <InfoValue>
                                    {uploadResult.missing_required?.length
                                        ? uploadResult.missing_required.join(', ')
                                        : t('Imports.none')}
                                </InfoValue>
                            </InfoItem>
                        </InfoGrid>

                        {uploadResult.field_mapping && !analyzeResult && (
                            <FieldMappingPanel>
                                <FieldMappingHeading>
                                    <div>
                                        <h4>{t('Imports.fieldMappingTitle', { defaultValue: 'Provjera kolona' })}</h4>
                                    <p>{t('Imports.fieldMappingIntro')}</p>
                                </div>
                                    <FieldCount>{t('Imports.fieldMappingRequiredCount', { mapped: requiredMappedCount, total: CSV_REQUIRED_FIELDS.length + 1 })}</FieldCount>
                                </FieldMappingHeading>
                                <FieldMappingGroupTitle>{t('Imports.fieldMappingRequiredTitle')}</FieldMappingGroupTitle>
                                <FieldMappingHint>{t('Imports.fieldMappingRequiredHelp')}</FieldMappingHint>
                                <FieldMappingRows>
                                    {CSV_REQUIRED_FIELDS.map(renderFieldMappingRow)}
                                </FieldMappingRows>
                                <FieldMappingGroupTitle $spaced>{t('Imports.fieldMappingIdentifierTitle')}</FieldMappingGroupTitle>
                                <FieldMappingHint>{t('Imports.fieldMappingIdentifierHelp')}</FieldMappingHint>
                                <FieldMappingRows>
                                    {CSV_IDENTITY_FIELDS.map(renderFieldMappingRow)}
                                </FieldMappingRows>
                                {optionalFieldTargets.length > 0 && (
                                    <OptionalFields>
                                        <summary>{t('Imports.optionalFieldsTitle')} <span>{t('Imports.optionalFieldsCount', { count: optionalFieldTargets.length })}</span></summary>
                                        <FieldMappingHint>{t('Imports.optionalFieldsHelp')}</FieldMappingHint>
                                        <FieldMappingRows>
                                            {optionalFieldTargets.map(renderFieldMappingRow)}
                                        </FieldMappingRows>
                                    </OptionalFields>
                                )}
                                <ActionRow>
                                    <MainButton type="button" onClick={handleSaveFieldMapping} disabled={savingFields} icon={<MdSave />}>
                                        {savingFields ? t('Imports.fieldMappingSaving', { defaultValue: 'Saving mapping…' }) : t('Imports.fieldMappingSave', { defaultValue: 'Save mapping and continue' })}
                                    </MainButton>
                                </ActionRow>
                                {fieldMappingValidation && <NoteBox $tone="warn" role="alert">{fieldMappingValidation}</NoteBox>}
                                {loading && !savingFields && (
                                    <FieldMappingHint role="status" aria-live="polite">
                                        {loadingStage || t('Imports.loading')} {t('Imports.wizard.processing.canContinue', { defaultValue: 'Možeš nastaviti čim su obavezna polja povezana.' })}
                                    </FieldMappingHint>
                                )}
                                <HelpText>{t('Imports.fieldMappingReusable', { defaultValue: 'After you confirm it, this mapping is reused by this organization for future CSV files with the same headers.' })}</HelpText>
                            </FieldMappingPanel>
                        )}

                        {/* Nepoznati stupci nisu greska - izvoz cesto nosi i
                            stupce za trgovcev vlastiti sustav - ali neka se vidi
                            da ih ne citamo. */}
                        {uploadResult.unknown_columns?.length > 0 && (
                            <NoteBox>
                                {t('Imports.unknownColumns', { columns: uploadResult.unknown_columns.join(', ') })}
                            </NoteBox>
                        )}
                        {!analyzeResult && uploadResult.status === 'validated' && uploadResult.field_mapping_reused && (
                            <ActionRow>
                                <MainButton variant="secondary" type="button" onClick={handleAnalyze} disabled={loading}>
                                    {t('Imports.analyzeButton')}
                                </MainButton>
                            </ActionRow>
                        )}
                    </StyledCard>
                )}

                {/* Razlika prije potvrde. Broj procitanih redaka trgovcu ne
                    znaci nista; zanima ga sto je novo, sto vec ima i gdje se
                    promijenila cijena. */}
                {analyzeResult?.diff && wizardStep === 4 && (
                    <StyledCard style={{ marginTop: '1.25rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>{t('Imports.diff.title')}</h3>
                            <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.95rem' }}>{t('Imports.diff.subtitle')}</p>
                        </div>

                        <DiffGrid>
                            <DiffTile $tone="new">
                                <DiffValue>{analyzeResult.diff.new_products}</DiffValue>
                                <DiffLabel>{t('Imports.diff.newProducts')}</DiffLabel>
                                {/* "Nemam ga ja" i "nema ga nigdje" su dva
                                    razlicita odgovora: vecina "novih" se veze na
                                    artikl koji katalog vec ima, umjesto da
                                    nastane duplikat. */}
                                {analyzeResult.diff.linking_to_known > 0 && (
                                    <DiffSub>
                                        {t('Imports.diff.linkingToKnown', { count: analyzeResult.diff.linking_to_known })}
                                    </DiffSub>
                                )}
                                {analyzeResult.diff.brand_new > 0 && (
                                    <DiffSub>
                                        {t('Imports.diff.brandNew', { count: analyzeResult.diff.brand_new })}
                                    </DiffSub>
                                )}
                            </DiffTile>
                            <DiffTile $tone="existing">
                                <DiffValue>{analyzeResult.diff.existing_products}</DiffValue>
                                <DiffLabel>{t('Imports.diff.existingProducts')}</DiffLabel>
                            </DiffTile>
                            <DiffTile $tone={analyzeResult.diff.price_changes > 0 ? 'changed' : undefined}>
                                <DiffValue>{analyzeResult.diff.price_changes}</DiffValue>
                                <DiffLabel>{t('Imports.diff.priceChanges')}</DiffLabel>
                            </DiffTile>
                            <DiffTile>
                                <DiffValue>{analyzeResult.diff.without_barcode}</DiffValue>
                                <DiffLabel>{t('Imports.diff.withoutBarcode')}</DiffLabel>
                            </DiffTile>
                            <DiffTile $tone={analyzeResult.diff.invalid > 0 ? 'bad' : undefined}>
                                <DiffValue>{analyzeResult.diff.invalid}</DiffValue>
                                <DiffLabel>{t('Imports.diff.invalid')}</DiffLabel>
                            </DiffTile>
                        </DiffGrid>

                        {analyzeResult.new_manufacturers?.length > 0 && (
                            <NoteBox $tone="warn">
                                <strong>{t('Imports.diff.newManufacturers')}</strong>{' '}
                                {analyzeResult.new_manufacturers
                                    .map((m) => `${m.name} (${m.count})`)
                                    .join(', ')}
                            </NoteBox>
                        )}

                        {analyzeResult.unmappable_categories?.length > 0 && (
                            <NoteBox $tone="warn">
                                <strong>{t('Imports.diff.unmappableCategories')}</strong>{' '}
                                {analyzeResult.unmappable_categories
                                    .map((c) => `${c.name} (${c.count})`)
                                    .join(', ')}
                            </NoteBox>
                        )}

                        {analyzeResult.invalid_rows?.length > 0 && (
                            <>
                                <SectionTitle>{t('Imports.diff.invalidRows')}</SectionTitle>
                                <DiffTable>
                                    <thead>
                                        <tr>
                                            <th>{t('Imports.diff.line')}</th>
                                            <th>{t('Imports.diff.product')}</th>
                                            <th>{t('Imports.diff.problem')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analyzeResult.invalid_rows.map((r) => (
                                            <tr key={`bad-${r.line}`}>
                                                <td>{r.line}</td>
                                                <td>{r.product_name || '—'}</td>
                                                <td>{r.issues.join('; ')}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </DiffTable>
                            </>
                        )}

                        {analyzeResult.price_changes?.length > 0 && (
                            <>
                                <SectionTitle>{t('Imports.diff.priceChangeList')}</SectionTitle>
                                <DiffTable>
                                    <thead>
                                        <tr>
                                            <th>{t('Imports.diff.product')}</th>
                                            <th>{t('Imports.diff.from')}</th>
                                            <th>{t('Imports.diff.to')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {analyzeResult.price_changes.map((r) => (
                                            <tr key={`price-${r.line}`}>
                                                <td>{r.product_name}</td>
                                                <td>{r.from}</td>
                                                <td>
                                                    <PriceTo $up={r.direction === 'up'}>
                                                        {r.direction === 'up' ? '▲' : '▼'} {r.to}
                                                    </PriceTo>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </DiffTable>
                            </>
                        )}
                    </StyledCard>
                )}

                {analyzeResult && (wizardStep === 3 || wizardStep === 4) && (
                    <StyledCard style={{ marginTop: '1.25rem' }}>
                        <div style={{ marginBottom: '1rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>{t('Imports.analysisTitle')}</h3>
                            <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.95rem' }}>{t('Imports.analysisSubtitle')}</p>
                        </div>

                        {wizardStep === 4 && <InfoGrid>
                            <InfoItem>
                                <InfoLabel>{t('Imports.manufacturers')}</InfoLabel>
                                <InfoValue>{analyzeResult.manufacturers?.length || 0}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('Imports.categories')}</InfoLabel>
                                <InfoValue>{analyzeResult.categories?.length || 0}</InfoValue>
                            </InfoItem>
                            <InfoItem>
                                <InfoLabel>{t('Imports.products')}</InfoLabel>
                                <InfoValue>{analyzeResult.products || 0}</InfoValue>
                            </InfoItem>
                            {(importType === 'catalog' || importType === 'discounts') && (
                                <InfoItem>
                                    <InfoLabel>{t('Imports.discounts')}</InfoLabel>
                                    <InfoValue>{analyzeResult.discounts || 0}</InfoValue>
                                </InfoItem>
                            )}
                            {(importType === 'catalog' || importType === 'discounts') && (
                                <>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.discountMatches.matched')}</InfoLabel>
                                        <InfoValue>{analyzeResult.discount_matches?.matched || 0}</InfoValue>
                                    </InfoItem>
                                    <InfoItem>
                                        <InfoLabel>{t('Imports.discountMatches.unmatched')}</InfoLabel>
                                        <InfoValue>{analyzeResult.discount_matches?.unmatched || 0}</InfoValue>
                                    </InfoItem>
                                </>
                            )}
                        </InfoGrid>}
                        {wizardStep === 4 && (importType === 'catalog' || importType === 'discounts') && (analyzeResult.discount_preview || []).length > 0 && (
                            <>
                                <SectionTitle>{t('Imports.discountPreview.title')}</SectionTitle>
                                <DiscountTableWrap>
                                    <DiscountTable>
                                        <thead>
                                            <tr>
                                                <th>{t('Imports.discountPreview.row')}</th>
                                                <th>{t('Imports.discountPreview.externalProductId')}</th>
                                                <th>{t('Imports.discountPreview.sku')}</th>
                                                <th>{t('Imports.discountPreview.percentage')}</th>
                                                <th>{t('Imports.discountPreview.value')}</th>
                                                <th>{t('Imports.discountPreview.discountedPrice')}</th>
                                                <th>{t('Imports.discountPreview.startAt')}</th>
                                                <th>{t('Imports.discountPreview.endAt')}</th>
                                                <th>{t('Imports.discountPreview.match')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(analyzeResult.discount_preview || []).map((row) => (
                                                <tr key={`discount-preview-${row.row_number}`}>
                                                    <td>{row.row_number}</td>
                                                    <td>{row.external_product_id || '-'}</td>
                                                    <td>{row.sku_or_gtin || '-'}</td>
                                                    <td>{row.discount_percentage || '-'}</td>
                                                    <td>{row.discount_value || '-'}</td>
                                                    <td>{row.discounted_price || '-'}</td>
                                                    <td>{row.discount_start_at || '-'}</td>
                                                    <td>{row.discount_end_at || '-'}</td>
                                                    <td>{row.matched ? t('Imports.discountPreview.matchYes') : t('Imports.discountPreview.matchNo')}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </DiscountTable>
                                </DiscountTableWrap>
                            </>
                        )}
                        {(requiresMappings || requiresVenueMappings) && wizardStep === 3 && (
                            <>
                                <SectionTitle>{t('Imports.mappingTitle')}</SectionTitle>
                                <MappingBlock>
                                    <MappingTitle>{t('Imports.manufacturerMapping')}</MappingTitle>
                                    {(analyzeResult.manufacturers || []).filter((item) => !item.mapped_id).map((item) => {
                                        const key = item.external_id || item.name;
                                        return (
                                            <MappingRow key={key}>
                                                <MappingSource>
                                                    <strong>{item.name || t('Imports.unknown')}</strong>
                                                    <MappingMeta>{item.external_id || t('Imports.noExternalId')}</MappingMeta>
                                                    {/* Sto je sustav poklopio sam neka se vidi, da se
                                                        ne cini kao propust cekanja na odabir. */}
                                                    {item.mapped_source === 'exact_name' && (
                                                        <AutoBadge>{t('Imports.autoMatched')}</AutoBadge>
                                                    )}
                                                </MappingSource>
                                                <MappingSelect
                                                    value={manufacturerSelectOptions.find((opt) => opt.value === manufacturerMappings[key]) || null}
                                                    onChange={(_, __, option) => {
                                                        setMappingSaved(false);
                                                        setManufacturerMappings((prev) => ({
                                                            ...prev,
                                                            [key]: option ? option.value : null,
                                                        }));
                                                    }}
                                                    options={manufacturerSelectOptions}
                                                    placeholder={t('Imports.selectManufacturer')}
                                                    isClearable
                                                />
                                            </MappingRow>
                                        );
                                    })}
                                </MappingBlock>
                                <MappingBlock>
                                    <MappingTitle>{t('Imports.categoryMapping')}</MappingTitle>
                                    {suggestedCategoryCount > 0 && (
                                        <HelpText>
                                            {t('Imports.wizard.categorySuggestionsReady', {
                                                count: suggestedCategoryCount,
                                                defaultValue: `Gemini je pripremio ${suggestedCategoryCount} prijedloga. Pouzdana mapiranja (90%+) su već odabrana; ostale prijedloge pregledaj. Sve izbore potvrđuješ jednim klikom.`,
                                            })}
                                        </HelpText>
                                    )}
                                    {(analyzeResult.categories || []).filter((item) => {
                                        const key = item.external_id || item.name;
                                        return !item.auto_resolved && !item.mapped_id;
                                    }).map((item) => {
                                        const key = item.external_id || item.name;
                                        const suggestionOption = item.suggested_mapping ? {
                                            value: `gpt:${item.suggested_mapping.id}`,
                                            label: `${item.suggested_mapping.path || item.suggested_mapping.name} · GPT ${item.suggested_mapping.gpt_code}`,
                                        } : null;
                                        return (
                                            <MappingRow key={key}>
                                                <MappingSource>
                                                    <strong>{item.name || t('Imports.unknown')}</strong>
                                                    <MappingMeta>{item.external_id || t('Imports.noExternalId')}</MappingMeta>
                                                    {item.mapped_source === 'resolver' && (
                                                        <AutoBadge>{t('Imports.autoMatched')}</AutoBadge>
                                                    )}
                                                    {/* Ove dvije uvoz svrsta, ali ne na jednu kategoriju
                                                        koja bi stala u izbornik - UPS nema most do core-a,
                                                        a "Periferija" se rjesava po proizvodu. */}
                                                    {item.mapped_source === 'resolver_gpt' && (
                                                        <AutoBadge>{t('Imports.autoGoogle')}</AutoBadge>
                                                    )}
                                                    {item.mapped_source === 'per_product' && (
                                                        <AutoBadge>{t('Imports.autoPerProduct')}</AutoBadge>
                                                    )}
                                                    {item.suggested_mapping && (
                                                        <MappingMeta>
                                                            {t('Imports.aiCategorySuggestion', {
                                                                path: item.suggested_mapping.path || item.suggested_mapping.name,
                                                                confidence: item.suggested_mapping.confidence ?? '—',
                                                                status: item.suggested_mapping.status,
                                                            })}
                                                        </MappingMeta>
                                                    )}
                                                    {item.suggested_mapping && categoryMappings[key] !== suggestionOption.value && (
                                                        <MainButton
                                                            variant="secondary"
                                                            type="button"
                                                            onClick={() => {
                                                                setMappingSaved(false);
                                                                setCategoryMappings((prev) => ({ ...prev, [key]: suggestionOption.value }));
                                                                setCategoryOptionSelections((prev) => ({ ...prev, [key]: suggestionOption }));
                                                            }}
                                                        >
                                                            {t('Imports.useAiCategorySuggestion')}
                                                        </MainButton>
                                                    )}
                                                </MappingSource>
                                                <GptCategorySearchSelect
                                                    value={categoryOptionSelections[key] || (categoryMappings[key] === suggestionOption?.value ? suggestionOption : null)}
                                                    placeholder={t('Imports.searchGptCategory', { defaultValue: 'Pretraži Google Product Taxonomy…' })}
                                                    messages={{
                                                        minChars: t('Imports.categorySearchMinChars', { defaultValue: 'Unesi najmanje 2 znaka za pretragu.' }),
                                                        searching: t('Imports.categorySearchLoading', { defaultValue: 'Pretražujemo GPT kategorije…' }),
                                                        noResults: t('Imports.categorySearchNoResults', { defaultValue: 'Nema pronađenih kategorija.' }),
                                                        failed: t('Imports.categorySearchFailed', { defaultValue: 'Pretraga nije uspjela. Provjeri vezu i pokušaj ponovo.' }),
                                                        clear: t('Imports.categorySearchClear', { defaultValue: 'Ukloni odabranu kategoriju' }),
                                                    }}
                                                    onChange={(option) => {
                                                        setMappingSaved(false);
                                                        setCategoryMappings((prev) => ({
                                                            ...prev,
                                                            [key]: option?.value || null,
                                                        }));
                                                        setCategoryOptionSelections((prev) => ({
                                                            ...prev,
                                                            [key]: option,
                                                        }));
                                                    }}
                                                />
                                            </MappingRow>
                                        );
                                    })}
                                </MappingBlock>
                                {/* Lokacije iz istog kataloskog CSV-a mapiraju se prije uvoza. */}
                                {hasVenues && (
                                    <MappingBlock>
                                        <MappingTitle>{t('Imports.venueMapping')}</MappingTitle>
                                        {(analyzeResult.venues || []).map((item) => {
                                            const m = venueMappings[item.external_id] || {};
                                            const isCreating = m.action === 'create';
                                            const currentSelectValue = m.action === 'map'
                                                ? venueSelectOptions.find((o) => o.value === m.venue_id) || null
                                                : m.action === 'create'
                                                ? { value: '__create__', label: t('Imports.createVenue') }
                                                : null;

                                            return (
                                                <div key={item.external_id}>
                                                    <MappingRow>
                                                        <MappingSource>
                                                            <strong>{item.name || t('Imports.unknown')}</strong>
                                                            <MappingMeta>
                                                                {item.external_id}
                                                                {item.city ? ` · ${item.city}` : ''}
                                                                {` · ${item.rows_count || 0} rows`}
                                                            </MappingMeta>
                                                            {item.status === 'matched' && (
                                                                <MatchBadge>
                                                                    ✓ {t('Imports.autoMatched')}: {item.matched_venue_name}
                                                                </MatchBadge>
                                                            )}
                                                        </MappingSource>
                                                        <MappingSelect
                                                            value={currentSelectValue}
                                                            onChange={(_, __, option) => {
                                                                const isCreate = option?.value === '__create__';
                                                                setVenueMappings((prev) => ({
                                                                    ...prev,
                                                                    [item.external_id]: isCreate
                                                                        ? { action: 'create', name: item.name || '', city: item.city || '', address: '', country_id: null }
                                                                        : option
                                                                        ? { action: 'map', venue_id: option.value }
                                                                        : { action: null, name: item.name || '', city: item.city || '', address: '', country_id: null },
                                                                }));
                                                            }}
                                                            options={venueSelectOptions}
                                                            placeholder={t('Imports.selectVenue')}
                                                            isClearable
                                                        />
                                                    </MappingRow>
                                                    {isCreating && (
                                                        <VenueCreateForm>
                                                            <VenueFormRow>
                                                                <VenueFormField>
                                                                    <VenueFormLabel>{t('Imports.venueName')} *</VenueFormLabel>
                                                                    <VenueFormInput
                                                                        value={m.name || ''}
                                                                        onChange={(e) => setVenueMappings((prev) => ({
                                                                            ...prev,
                                                                            [item.external_id]: { ...prev[item.external_id], name: e.target.value },
                                                                        }))}
                                                                        placeholder={t('Imports.venueNamePlaceholder')}
                                                                    />
                                                                </VenueFormField>
                                                                <VenueFormField>
                                                                    <VenueFormLabel>{t('Imports.venueCity')}</VenueFormLabel>
                                                                    <VenueFormInput
                                                                        value={m.city || ''}
                                                                        onChange={(e) => setVenueMappings((prev) => ({
                                                                            ...prev,
                                                                            [item.external_id]: { ...prev[item.external_id], city: e.target.value },
                                                                        }))}
                                                                        placeholder={t('Imports.venueCityPlaceholder')}
                                                                    />
                                                                </VenueFormField>
                                                                <VenueFormField>
                                                                    <VenueFormLabel>{t('Imports.venueAddress')}</VenueFormLabel>
                                                                    <VenueFormInput
                                                                        value={m.address || ''}
                                                                        onChange={(e) => setVenueMappings((prev) => ({
                                                                            ...prev,
                                                                            [item.external_id]: { ...prev[item.external_id], address: e.target.value },
                                                                        }))}
                                                                        placeholder={t('Imports.venueAddressPlaceholder')}
                                                                    />
                                                                </VenueFormField>
                                                            </VenueFormRow>
                                                        </VenueCreateForm>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </MappingBlock>
                                )}
                                <ActionRow>
                                    <MainButton variant="secondary" type="button" onClick={handleSaveMappings} disabled={loading} icon={mappingSaved ? <MdCheckCircle /> : null}>
                                        {mappingSaved ? t('Imports.mappingsSaved') : t('Imports.saveMappings')}
                                    </MainButton>
                                </ActionRow>
                            </>
                        )}
                    </StyledCard>
                )}

                {canRunImport && wizardStep === 4 && (
                    <RunCard>
                        <RunHeading>
                            <div>
                                <h3>{t('Imports.runTitle')}</h3>
                                <p>{t(`Imports.runSubtitleByType.${importType}`)}</p>
                            </div>
                            {!progress && !runSummary && <MainButton type="button" onClick={handleRun} disabled={loading}>
                                {loading ? t('Imports.running') : t('Imports.runButton')}
                            </MainButton>}
                        </RunHeading>
                        {(progress || runSummary) && (
                            <>
                                {['queued', 'processing'].includes(progress?.import_status || progress?.status || runSummary?.status) && (
                                    <RunProgress role="status" aria-live="polite">
                                        <RunProgressTop>
                                            <div>
                                                <ProgressLabel>
                                                    {(progress?.import_status || progress?.status || runSummary?.status) === 'queued'
                                                        ? t('Imports.wizard.processing.importQueued', { defaultValue: 'CSV je u redu za obradu.' })
                                                        : t('Imports.wizard.processing.importProcessing', { defaultValue: 'CSV se obrađuje u pozadini.' })}
                                                </ProgressLabel>
                                                <ProgressSubtext>
                                                    {t('Imports.wizard.processing.importProgress', { defaultValue: 'Napredak se osvježava automatski.' })}
                                                </ProgressSubtext>
                                            </div>
                                            <ProgressPercent>{importProgressPercent}%</ProgressPercent>
                                        </RunProgressTop>
                                        <ProgressTrack aria-label={`${importProgressPercent}%`}>
                                            <ProgressFill $value={importProgressPercent} />
                                        </ProgressTrack>
                                        <ProgressCount>{importProcessedRows.toLocaleString()} / {importTotalRows.toLocaleString()} {t('Imports.processedRows')}</ProgressCount>
                                    </RunProgress>
                                )}
                                {progress?.import_status === 'staged' && <RunFinished role="status">
                                    <MdCheckCircle size={19} />
                                    <span>{t('Imports.importStaged', { defaultValue: 'CSV obrada završena; stavke su u katalogu za daljnju obradu.' })}</span>
                                </RunFinished>}
                                <RunSummaryGrid>
                                    <RunSummaryItem><span>{t('Imports.status')}</span><strong>{progress?.import_status === 'staged'
                                        ? t('Imports.completed', { defaultValue: 'Završeno' })
                                        : progress?.import_status || progress?.status || runSummary?.status || t('Imports.pending')}</strong></RunSummaryItem>
                                    <RunSummaryItem><span>{t('Imports.processedRows')}</span><strong>{importProcessedRows.toLocaleString()}</strong></RunSummaryItem>
                                    <RunSummaryItem><span>{t('Imports.failed')}</span><strong>{progress?.failed_count ?? runSummary?.failed ?? 0}</strong></RunSummaryItem>
                                </RunSummaryGrid>
                                <RunDetails>
                                    <summary>{t('Imports.progressTitle')} · {t('Imports.details', { defaultValue: 'detalji' })}</summary>
                                    <InfoGrid>
                                        <InfoItem><InfoLabel>{t('Imports.stagedProducts', { defaultValue: 'Stavke za daljnju obradu' })}</InfoLabel><InfoValue>{progress?.staged_count ?? runSummary?.staged ?? 0}</InfoValue></InfoItem>
                                        <InfoItem><InfoLabel>{t('Imports.created')}</InfoLabel><InfoValue>{progress?.created_count ?? runSummary?.created ?? 0}</InfoValue></InfoItem>
                                        <InfoItem><InfoLabel>{t('Imports.updated')}</InfoLabel><InfoValue>{progress?.updated_count ?? runSummary?.updated ?? 0}</InfoValue></InfoItem>
                                        <InfoItem><InfoLabel>{t('Imports.skipped')}</InfoLabel><InfoValue>{progress?.skipped_count ?? runSummary?.skipped ?? 0}</InfoValue></InfoItem>
                                    </InfoGrid>
                                </RunDetails>
                            </>
                        )}
                    </RunCard>
                )}
            </PageContent>
        </PageContainer>
    );
};

const GptCategorySearchSelect = ({ value, onChange, placeholder, messages }) => {
    const [query, setQuery] = useState('');
    const [options, setOptions] = useState([]);
    const [searchState, setSearchState] = useState('idle');

    useEffect(() => {
        const normalizedQuery = query.trim();
        if (normalizedQuery.length < 2) {
            setOptions([]);
            setSearchState('idle');
            return undefined;
        }

        let cancelled = false;
        setSearchState('loading');
        const timeout = setTimeout(async () => {
            try {
                const categories = await searchGoogleProductCategories(normalizedQuery);
                if (cancelled) return;
                const nextOptions = categories.map((category) => ({
                    value: `gpt:${category.id}`,
                    label: `${category.path || category.name} · GPT ${category.gpt_code}`,
                }));
                setOptions(nextOptions);
                setSearchState(nextOptions.length ? 'results' : 'empty');
            } catch {
                if (cancelled) return;
                setOptions([]);
                setSearchState('error');
            }
        }, 250);

        return () => {
            cancelled = true;
            clearTimeout(timeout);
        };
    }, [query]);

    const statusMessage = searchState === 'loading'
        ? messages.searching
        : searchState === 'empty'
            ? messages.noResults
            : searchState === 'error'
                ? messages.failed
                : query.trim().length < 2
                    ? messages.minChars
                    : '';

    return (
        <CategorySearchWrap>
            {value && (
                <SelectedCategory>
                    <span>{value.label}</span>
                    <ClearCategoryButton type="button" onClick={() => onChange(null)} aria-label={messages.clear}>×</ClearCategoryButton>
                </SelectedCategory>
            )}
            <CategorySearchInput
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder={placeholder}
                aria-label={placeholder}
                aria-autocomplete="list"
                aria-expanded={options.length > 0}
            />
            {statusMessage && <CategorySearchStatus role={searchState === 'error' ? 'alert' : 'status'}>{statusMessage}</CategorySearchStatus>}
            {options.length > 0 && (
                <CategorySearchResults role="listbox">
                    {options.map((option) => (
                        <CategorySearchOption
                            key={option.value}
                            type="button"
                            role="option"
                            aria-selected={value?.value === option.value}
                            onClick={() => {
                                onChange(option);
                                setQuery('');
                                setOptions([]);
                                setSearchState('idle');
                            }}
                        >
                            {option.label}
                        </CategorySearchOption>
                    ))}
                </CategorySearchResults>
            )}
        </CategorySearchWrap>
    );
};

export default ImportsPage;

const TemplateRow = styled.div`
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin: 0.75rem 0;
`;

const TemplateLink = styled.a`
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.5rem 0.85rem;
    border: 1px solid rgba(130, 22, 146, 1);
    border-radius: 6px;
    color: rgba(130, 22, 146, 1);
    font-size: 0.88rem;
    font-weight: 600;
    text-decoration: none;

    &:hover {
        background: rgba(130, 22, 146, 1);
        color: #fff;
    }
`;

const NoteBox = styled.div`
    margin-top: 0.85rem;
    padding: 0.65rem 0.8rem;
    border-radius: 6px;
    font-size: 0.86rem;
    line-height: 1.5;
    background: ${({ $tone }) => ($tone === 'warn' ? '#fff8e1' : '#f3f4f6')};
    color: ${({ $tone }) => ($tone === 'warn' ? '#8a6d00' : '#4b5563')};
`;

const DiffGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 0.75rem;
`;

/* Boja nosi znacenje samo uz brojku i natpis, nikad sama: sivo je neutralno,
   zuto trazi pogled, crveno znaci da redak nece uci. */
const DiffTile = styled.div`
    padding: 0.85rem;
    border-radius: 8px;
    border: 1px solid ${({ $tone }) => ({
        new: '#c7e0c9',
        existing: '#cfd8e3',
        changed: '#f0dca8',
        bad: '#eec2c2',
    }[$tone] || '#e5e7eb')};
    background: ${({ $tone }) => ({
        new: '#f2f9f3',
        existing: '#f5f7fa',
        changed: '#fffaf0',
        bad: '#fdf4f4',
    }[$tone] || '#fff')};
`;

const DiffValue = styled.div`
    font-size: 1.5rem;
    font-weight: 700;
    color: #111827;
`;

const DiffSub = styled.div`
    font-size: 0.74rem;
    color: #6b7280;
    margin-top: 0.3rem;
`;

const DiffLabel = styled.div`
    font-size: 0.8rem;
    color: #6b7280;
    margin-top: 0.15rem;
`;

const DiffTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 1rem;

    th, td {
        text-align: left;
        padding: 0.5rem;
        border-bottom: 1px solid #eceff3;
        font-size: 0.86rem;
    }

    th {
        color: #6b7280;
        font-weight: 600;
    }
`;

const PriceTo = styled.span`
    font-weight: 600;
    color: ${({ $up }) => ($up ? '#b3261e' : '#1b7f3b')};
`;

const WizardProgress = styled.nav`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.35rem;
    margin: 0 0 0.85rem;
    padding: 0;

    @media (max-width: 720px) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
`;

const WizardStep = styled.div`
    display: flex;
    align-items: center;
    gap: 0.45rem;
    min-width: 0;
    padding: 0.55rem 0.35rem;
    border-bottom: 2px solid ${({ $active, $complete }) => ($active || $complete ? '#821692' : '#e8e5ea')};
    color: ${({ $active, $complete }) => ($active || $complete ? '#61136f' : '#77717b')};
    font-size: 0.78rem;
    font-weight: ${({ $active }) => ($active ? 700 : 500)};

    span:last-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
`;

const WizardStepNumber = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 1.35rem;
    width: 1.35rem;
    height: 1.35rem;
    border-radius: 50%;
    color: white;
    background: ${({ $active, $complete }) => ($active || $complete ? '#821692' : '#b8b2bd')};
    font-size: 0.72rem;
    font-weight: 700;
`;

const ProgressPanel = styled.div`
    display: flex;
    align-items: center;
    gap: 0.9rem;
    margin: 1rem 0;
    padding: 1rem 1.15rem;
    border: 1px solid #e5d5ec;
    border-radius: 12px;
    background: #fbf6fd;
    color: #4d155c;
`;

const ProgressSpinner = styled.span`
    width: 1.35rem;
    height: 1.35rem;
    flex: 0 0 1.35rem;
    border: 3px solid #eadcf0;
    border-top-color: #811a91;
    border-radius: 50%;
    animation: import-spin 0.8s linear infinite;

    @keyframes import-spin { to { transform: rotate(360deg); } }
`;

const ProgressLabel = styled.div`
    font-size: 0.95rem;
    font-weight: 700;
`;

const ProgressSubtext = styled.div`
    margin-top: 0.2rem;
    color: #74677b;
    font-size: 0.81rem;
`;

const UploadCard = styled(StyledCard)`
    padding: clamp(1.1rem, 2.4vw, 1.8rem);
`;

const UploadHeading = styled.div`
    display: flex;
    align-items: center;
    gap: 0.85rem;
    margin-bottom: 1.15rem;

    h3 { margin: 0; color: #211b25; font-size: 1.15rem; }
    p { margin: 0.25rem 0 0; color: #726c77; font-size: 0.88rem; }
`;

const UploadIcon = styled.span`
    display: grid;
    place-items: center;
    width: 2.8rem;
    height: 2.8rem;
    flex: 0 0 2.8rem;
    border-radius: 12px;
    background: #f6edf8;
    color: #821692;
`;

const UploadForm = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 0.75rem;
    align-items: stretch;

    @media (max-width: 620px) { grid-template-columns: 1fr; }
`;

const UploadDropzone = styled.label`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    min-width: 0;
    min-height: 66px;
    padding: 0.8rem 1rem;
    border: 1px dashed #c9b6d0;
    border-radius: 12px;
    background: #fcf9fd;
    cursor: pointer;
    transition: border-color 120ms ease, background 120ms ease;

    &:hover { border-color: #821692; background: #faf4fc; }
    &:focus-within { outline: 3px solid rgba(130, 22, 146, 0.15); }
`;

const UploadPrompt = styled.span`
    display: flex;
    flex-direction: column;
    min-width: 0;
    gap: 0.2rem;
    color: #312a36;

    strong { overflow: hidden; font-size: 0.9rem; text-overflow: ellipsis; white-space: nowrap; }
    span { color: #77717e; font-size: 0.78rem; }
`;

const UploadBrowse = styled.span`
    flex: 0 0 auto;
    color: #71147f;
    font-size: 0.82rem;
    font-weight: 700;
`;

const TemplateDetails = styled.details`
    margin-top: 1rem;
    padding-top: 0.8rem;
    border-top: 1px solid #efebf1;

    summary { cursor: pointer; color: #5b176b; font-size: 0.83rem; font-weight: 650; }
    &[open] summary { margin-bottom: 0.55rem; }
`;

const RunCard = styled(StyledCard)`
    margin-top: 1rem;
    border: 1px solid #e8ddec;
    border-left: 3px solid #821692;
`;

const RunHeading = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.9rem;

    h3 { margin: 0; color: #211b25; font-size: 1.08rem; }
    p { margin: 0.25rem 0 0; color: #726c77; font-size: 0.84rem; }
    @media (max-width: 620px) { align-items: flex-start; flex-direction: column; }
`;

const RunProgress = styled.div`
    padding: 0.9rem 1rem;
    border: 1px solid #eee5f1;
    border-radius: 11px;
    background: #fcf9fd;
`;

const RunProgressTop = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
`;

const ProgressPercent = styled.strong`
    color: #68147a;
    font-size: 1.05rem;
`;

const ProgressTrack = styled.div`
    height: 7px;
    margin-top: 0.75rem;
    overflow: hidden;
    border-radius: 99px;
    background: #eae3ed;
`;

const ProgressFill = styled.div`
    width: ${({ $value }) => `${$value}%`};
    height: 100%;
    border-radius: inherit;
    background: linear-gradient(90deg, #8b1799, #5d35c7);
    transition: width 240ms ease;
`;

const ProgressCount = styled.div`
    margin-top: 0.45rem;
    color: #716a76;
    font-size: 0.77rem;
`;

const RunFinished = styled.div`
    display: flex;
    align-items: center;
    gap: 0.55rem;
    margin-top: 0.85rem;
    color: #247447;
    font-size: 0.85rem;
    font-weight: 600;
`;

const RunSummaryGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 0.6rem;
    margin-top: 0.85rem;
    @media (max-width: 520px) { grid-template-columns: 1fr; }
`;

const RunSummaryItem = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.65rem 0.75rem;
    border: 1px solid #efebf1;
    border-radius: 9px;
    color: #6e6873;
    font-size: 0.78rem;

    strong { color: #27212c; font-size: 0.84rem; }
`;

const RunDetails = styled.details`
    margin-top: 0.85rem;
    padding-top: 0.75rem;
    border-top: 1px solid #efebf1;

    summary { cursor: pointer; color: #5b176b; font-size: 0.8rem; font-weight: 650; }
    &[open] summary { margin-bottom: 0.8rem; }
`;

const FormRow = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
`;

const FileInput = styled.input`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    opacity: 0;
    cursor: pointer;
`;

const HelpText = styled.p`
    margin-top: 0.75rem;
    color: #6b7280;
    font-size: 0.875rem;
`;

const FieldMappingPanel = styled.section`
    margin-top: 1.25rem;
    padding: clamp(1rem, 2.5vw, 1.75rem);
    border: 1px solid #e9e1ed;
    border-radius: 16px;
    background: linear-gradient(180deg, #fff 0%, #fdfbfe 100%);
    box-shadow: 0 8px 24px rgba(47, 23, 56, 0.045);

    h4 { margin: 0 0 0.35rem; color: #25202a; }
    p { margin: 0; color: #62606a; font-size: 0.9rem; line-height: 1.45; }
`;

const FieldMappingHeading = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    margin-bottom: 1.5rem;

    @media (max-width: 640px) {
        flex-direction: column;
    }
`;

const FieldCount = styled.span`
    flex: 0 0 auto;
    padding: 0.4rem 0.7rem;
    border-radius: 999px;
    background: #f4eaf6;
    color: #68147a;
    font-size: 0.78rem;
    font-weight: 700;
`;

const FieldMappingGroupTitle = styled.h5`
    margin: ${({ $spaced }) => ($spaced ? '1.35rem 0 0.35rem' : '0 0 0.35rem')};
    color: #28222e;
    font-size: 0.98rem;
    font-weight: 700;
`;

const FieldMappingHint = styled.p`
    margin: 0 0 0.9rem !important;
    color: #716b78 !important;
    font-size: 0.82rem !important;
`;

const FieldMappingRows = styled.div`
    display: grid;
    gap: 0.65rem;
`;

const FieldMappingRow = styled.div`
    display: grid;
    grid-template-columns: minmax(180px, 0.8fr) minmax(220px, 1.2fr);
    align-items: center;
    gap: 1rem;
    padding: 0.85rem 1rem;
    border: 1px solid #eee8f1;
    border-radius: 11px;
    background: #fff;

    @media (max-width: 640px) {
        grid-template-columns: 1fr;
        gap: 0.55rem;
    }
`;

const FieldMappingDetails = styled.div`
    min-width: 0;
`;

const FieldMappingLabel = styled.div`
    color: #302936;
    font-size: 0.88rem;
    font-weight: 650;
    text-transform: capitalize;
`;

const FieldExample = styled.div`
    margin-top: 0.25rem;
    overflow: hidden;
    color: #77717e;
    font-size: 0.77rem;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const FieldMappingSelect = styled.select`
    width: 100%;
    min-width: 0;
    min-height: 42px;
    padding: 0.55rem 0.75rem;
    border: 1px solid #d9d0df;
    border-radius: 9px;
    background: #fff;
    color: #28232c;
    font-size: 0.88rem;
    &:focus { outline: 3px solid rgba(139, 23, 153, 0.15); border-color: #8b1799; }
`;

const OptionalFields = styled.details`
    margin-top: 1.1rem;
    padding-top: 1rem;
    border-top: 1px solid #ece6ef;

    summary {
        display: flex;
        justify-content: space-between;
        cursor: pointer;
        color: #5b176b;
        font-size: 0.88rem;
        font-weight: 700;
    }
    summary span { color: #817987; font-size: 0.78rem; font-weight: 500; }
    &[open] summary { margin-bottom: 0.8rem; }
    ${FieldMappingHint} { margin-top: 0.35rem !important; }
`;

const RequiredMark = styled.span`
    color: #b3261e;
`;

const FieldSuggestionMeta = styled.small`
    display: block;
    margin-top: 0.25rem;
    color: #6b5576;
    font-size: 0.72rem;
    font-weight: 400;
    line-height: 1.35;
    white-space: normal;
`;

const ColumnsGuide = styled.div`
    margin-top: 0.75rem;
    padding: 0.75rem;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
`;

const ColumnsGuideTitle = styled.div`
    font-size: 0.875rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 0.375rem;
`;

const ColumnsGuideText = styled.p`
    margin: 0.25rem 0;
    font-size: 0.875rem;
    color: #374151;
    line-height: 1.4;
`;

const ActionRow = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
`;

const InfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;
`;

const InfoItem = styled.div`
    background: #f9fafb;
    border-radius: 12px;
    padding: 1rem;
    border: 1px solid #f3f4f6;
`;

const InfoLabel = styled.div`
    font-size: 0.75rem;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    font-weight: 600;
    margin-bottom: 0.25rem;
`;

const InfoValue = styled.div`
    font-size: 1.125rem;
    color: #111827;
    font-weight: 600;
`;

const MappingBlock = styled.div`
    border-top: 1px solid #e5e7eb;
    padding-top: 1.25rem;
    margin-top: 1.25rem;
`;

const AutoBadge = styled.span`
    display: inline-block;
    margin-top: 0.25rem;
    padding: 0.12rem 0.45rem;
    border-radius: 4px;
    background: #e8f4ea;
    color: #1b7f3b;
    font-size: 0.72rem;
    font-weight: 600;
`;

const MappingTitle = styled.h4`
    margin: 0 0 1rem;
    color: #374151;
    font-size: 1rem;
`;

const MappingRow = styled.div`
    display: grid;
    grid-template-columns: 1fr minmax(240px, 320px);
    gap: 1rem;
    align-items: center;
    padding: 0.75rem 0;
    border-bottom: 1px solid #f3f4f6;
    
    &:last-child {
        border-bottom: none;
    }
`;

const MappingSource = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

const MappingMeta = styled.span`
    color: #9ca3af;
    font-size: 0.875rem;
`;

const MappingSelect = styled(SearchableSelect)``;

const DiscountTableWrap = styled.div`
    margin-top: 0.75rem;
    overflow-x: auto;
    border: 1px solid #e5e7eb;
    border-radius: 10px;
`;

const DiscountTable = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 0.875rem;

    th,
    td {
        padding: 0.625rem 0.75rem;
        border-bottom: 1px solid #f3f4f6;
        text-align: left;
        white-space: nowrap;
    }

    th {
        background: #f9fafb;
        color: #374151;
        font-weight: 600;
    }

    tbody tr:last-child td {
        border-bottom: none;
    }
`;

const MatchBadge = styled.span`
    display: inline-block;
    margin-top: 0.125rem;
    padding: 0.125rem 0.5rem;
    background: #dcfce7;
    color: #166534;
    border-radius: 99px;
    font-size: 0.75rem;
    font-weight: 600;
`;

const VenueCreateForm = styled.div`
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 1rem;
    margin: 0.25rem 0 0.75rem;
`;

const VenueFormRow = styled.div`
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
`;

const VenueFormField = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1;
    min-width: 160px;
`;

const VenueFormLabel = styled.label`
    font-size: 0.8125rem;
    color: #374151;
    font-weight: 600;
`;

const VenueFormInput = styled.input`
    padding: 0.5rem 0.625rem;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    font-size: 0.875rem;
    background: #fff;

    &:focus {
        border-color: #821692;
        outline: none;
        box-shadow: 0 0 0 2px rgba(130, 22, 146, 0.1);
    }
`;

const CategorySearchWrap = styled.div`
    position: relative;
    flex: 1;
    min-width: 250px;
`;

const CategorySearchInput = styled.input`
    width: 100%;
    min-height: 45px;
    padding: 0.625rem 0.75rem;
    border: 1px solid #ccc;
    border-radius: 6px;
    background: #fff;
    font: inherit;

    &:focus {
        border-color: rgba(130, 22, 146, 0.8);
        outline: none;
        box-shadow: 0 0 0 3px rgba(130, 22, 146, 0.12);
    }
`;

const CategorySearchStatus = styled.div`
    margin-top: 0.35rem;
    color: ${({ role }) => role === 'alert' ? '#b42318' : '#74677b'};
    font-size: 0.8rem;
`;

const CategorySearchResults = styled.div`
    position: absolute;
    z-index: 30;
    top: calc(100% + 0.25rem);
    left: 0;
    right: 0;
    max-height: 260px;
    overflow-y: auto;
    padding: 0.25rem;
    border: 1px solid #e5d5ec;
    border-radius: 8px;
    background: #fff;
    box-shadow: 0 8px 24px rgba(31, 18, 37, 0.14);
`;

const CategorySearchOption = styled.button`
    display: block;
    width: 100%;
    padding: 0.65rem 0.75rem;
    border: 0;
    border-radius: 5px;
    background: #fff;
    color: #24202a;
    text-align: left;
    cursor: pointer;

    &:hover, &:focus-visible {
        background: #f7eff9;
        outline: none;
    }
`;

const SelectedCategory = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    margin-bottom: 0.35rem;
    padding: 0.45rem 0.6rem;
    border: 1px solid #dfc7e7;
    border-radius: 6px;
    background: #fbf6fd;
    color: #4d155c;
    font-size: 0.8rem;
`;

const ClearCategoryButton = styled.button`
    flex: 0 0 auto;
    border: 0;
    background: transparent;
    color: #4d155c;
    font-size: 1.1rem;
    line-height: 1;
    cursor: pointer;
`;
